/**
 * @file SplitOrderModal.js
 * @version 1.2.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-02-23
 * @modified 2026-09-16
 * @description Multi-step modal for splitting a customer order across multiple customers
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import SearchableDropdown from './SearchableDropdown';
import LoadingSpinner from './LoadingSpinner';
import Textarea from './Textarea';
import { useSlideOutButton } from './SlideOutButton';
import { formatDecimalInput, formatQuantity } from '../utils/numberFormatters';
import './SplitOrderModal.css';

const STEPS = {
  LOADING: 'loading',
  SUPPLIED_QTY: 'supplied_qty',
  DELIVERED_QTY: 'delivered_qty',
  ALLOCATE: 'allocate',
  REVIEW: 'review',
  SPLITTING: 'splitting',
  RESULT: 'result'
};

function SplitOrderModal({ isOpen, onClose, orderId, onSuccess }) {
  const [step, setStep] = useState(STEPS.LOADING);
  const [order, setOrder] = useState(null);
  const [suppliedQuantities, setSuppliedQuantities] = useState({});
  const [deliveredQuantities, setDeliveredQuantities] = useState({});
  const [splits, setSplits] = useState([{ accountId: '', lines: {} }]);
  const [customers, setCustomers] = useState([]);
  const [splitNotes, setSplitNotes] = useState('');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useSlideOutButton([isOpen, step]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const loadOrder = useCallback(async () => {
    if (!orderId) return;
    setStep(STEPS.LOADING);
    setError(null);

    try {
      // Fetch order and customers in parallel
      const [orderRes, customersRes] = await Promise.all([
        fetch(`${API_URL}/api/orders/customer-orders/${orderId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=CUSTOMER&limit=10000`, { credentials: 'include' })
      ]);

      if (!orderRes.ok) throw new Error('Failed to load order');
      if (!customersRes.ok) throw new Error('Failed to load customers');

      const orderData = await orderRes.json();
      const customersData = await customersRes.json();

      setOrder(orderData);
      setCustomers((customersData.contacts || []).map(c => ({
        value: c.id.toString(),
        label: `${c.company_name}${c.account_no ? ` (${c.account_no})` : ''}`
      })));

      // Initialize supplied quantities
      const suppliedQtys = {};
      const deliveredQtys = {};
      let allHaveSupplied = true;

      if (orderData.lines) {
        orderData.lines.forEach(line => {
          if (line.supplied_quantity !== null && line.supplied_quantity !== undefined) {
            suppliedQtys[line.id] = formatDecimalInput(line.supplied_quantity);
            deliveredQtys[line.id] = formatDecimalInput(line.supplied_quantity);
          } else {
            suppliedQtys[line.id] = '';
            deliveredQtys[line.id] = '';
            allHaveSupplied = false;
          }
        });
      }

      setSuppliedQuantities(suppliedQtys);
      setDeliveredQuantities(deliveredQtys);
      setSplits([{ accountId: '', lines: {} }]);

      // Determine starting step
      if (allHaveSupplied) {
        setStep(STEPS.DELIVERED_QTY);
      } else {
        setStep(STEPS.SUPPLIED_QTY);
      }
    } catch (err) {
      setError(err.message);
      setStep(STEPS.RESULT);
    }
  }, [orderId]);

  useEffect(() => {
    if (isOpen && orderId) {
      loadOrder();
    }
  }, [isOpen, orderId, loadOrder]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setStep(STEPS.LOADING);
        setOrder(null);
        setSuppliedQuantities({});
        setDeliveredQuantities({});
        setSplits([{ accountId: '', lines: {} }]);
        setSplitNotes('');
        setError(null);
        setResult(null);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Save supplied quantities and advance
  const handleSaveSuppliedQty = async () => {
    setError(null);

    // Validate all lines have supplied qty
    const missingLines = order.lines.filter(l => !suppliedQuantities[l.id] || suppliedQuantities[l.id] === '');
    if (missingLines.length > 0) {
      setError('Please enter supplied quantity for all lines');
      return;
    }

    try {
      const lines = Object.entries(suppliedQuantities).map(([lineId, val]) => ({
        lineId: parseInt(lineId),
        suppliedQuantity: parseFloat(val)
      }));

      const res = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/supplied-quantity`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ lines })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save supplied quantities');
      }

      // Initialize delivered quantities to supplied quantities
      const deliveredQtys = {};
      Object.entries(suppliedQuantities).forEach(([lineId, val]) => {
        deliveredQtys[lineId] = val;
      });
      setDeliveredQuantities(deliveredQtys);

      setStep(STEPS.DELIVERED_QTY);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeliveredQtyNext = () => {
    setError(null);

    // Validate delivered quantities
    for (const line of order.lines) {
      const delivered = parseFloat(deliveredQuantities[line.id] || 0);
      const supplied = parseFloat(suppliedQuantities[line.id] || line.supplied_quantity || 0);
      if (delivered < 0) {
        setError('Delivered quantity cannot be negative');
        return;
      }
      if (delivered > supplied + 0.0001) {
        setError(`Delivered quantity for "${line.product_name}" cannot exceed supplied quantity`);
        return;
      }
    }

    // Check if there's any remaining to split
    const hasRemaining = order.lines.some(line => {
      const delivered = parseFloat(deliveredQuantities[line.id] || 0);
      const supplied = parseFloat(suppliedQuantities[line.id] || line.supplied_quantity || 0);
      return supplied - delivered > 0.0001;
    });

    if (!hasRemaining) {
      // No remaining - just update quantities, no split needed
      setError('All supplied quantity has been delivered to this customer. No split is needed.');
      return;
    }

    // Initialize first split with remaining quantities
    const initialLines = {};
    order.lines.forEach(line => {
      const delivered = parseFloat(deliveredQuantities[line.id] || 0);
      const supplied = parseFloat(suppliedQuantities[line.id] || line.supplied_quantity || 0);
      const remaining = supplied - delivered;
      if (remaining > 0.0001) {
        initialLines[line.id] = formatDecimalInput(remaining);
      }
    });

    setSplits([{ accountId: '', lines: initialLines }]);
    setStep(STEPS.ALLOCATE);
  };

  const handleSplitCustomerChange = (splitIndex, accountId) => {
    setSplits(prev => {
      const updated = [...prev];
      updated[splitIndex] = { ...updated[splitIndex], accountId };
      return updated;
    });
  };

  const handleSplitQtyChange = (splitIndex, lineId, value) => {
    setSplits(prev => {
      const updated = [...prev];
      updated[splitIndex] = {
        ...updated[splitIndex],
        lines: { ...updated[splitIndex].lines, [lineId]: value }
      };
      return updated;
    });
  };

  const handleAddSplit = () => {
    // Calculate remaining after existing splits
    const remainingLines = {};
    order.lines.forEach(line => {
      const delivered = parseFloat(deliveredQuantities[line.id] || 0);
      const supplied = parseFloat(suppliedQuantities[line.id] || line.supplied_quantity || 0);
      let allocated = 0;
      splits.forEach(split => {
        allocated += parseFloat(split.lines[line.id] || 0);
      });
      const remaining = supplied - delivered - allocated;
      if (remaining > 0.0001) {
        remainingLines[line.id] = formatDecimalInput(remaining);
      }
    });

    setSplits(prev => [...prev, { accountId: '', lines: remainingLines }]);
  };

  const handleRemoveSplit = (splitIndex) => {
    if (splits.length <= 1) return;
    setSplits(prev => prev.filter((_, i) => i !== splitIndex));
  };

  const getRemainingForLine = (lineId) => {
    const delivered = parseFloat(deliveredQuantities[lineId] || 0);
    const supplied = parseFloat(suppliedQuantities[lineId] || 0);
    let allocated = 0;
    splits.forEach(split => {
      allocated += parseFloat(split.lines[lineId] || 0);
    });
    return supplied - delivered - allocated;
  };

  const canProceedToReview = () => {
    // All splits must have a customer selected and at least one line with quantity
    return splits.every(split => {
      if (!split.accountId) return false;
      return Object.values(split.lines).some(qty => parseFloat(qty || 0) > 0);
    });
  };

  const handleConfirmSplit = async () => {
    setStep(STEPS.SPLITTING);
    setError(null);

    try {
      // Build request body
      const deliveredQtys = {};
      Object.entries(deliveredQuantities).forEach(([lineId, val]) => {
        deliveredQtys[lineId] = parseFloat(val);
      });

      const splitData = splits.map(split => ({
        accountId: parseInt(split.accountId),
        lines: Object.entries(split.lines)
          .filter(([_, qty]) => parseFloat(qty || 0) > 0)
          .map(([lineId, qty]) => ({
            lineId: parseInt(lineId),
            quantity: parseFloat(qty)
          }))
      }));

      const res = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/split`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ deliveredQuantities: deliveredQtys, splits: splitData, splitNotes })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to split order');

      setResult(data);
      setStep(STEPS.RESULT);
    } catch (err) {
      setError(err.message);
      setStep(STEPS.REVIEW);
    }
  };

  const handleClose = () => {
    if (result?.success) {
      onSuccess?.();
    }
    onClose();
  };

  const getStepTitle = () => {
    switch (step) {
      case STEPS.SUPPLIED_QTY: return 'Step 1: Confirm Supplied Quantity';
      case STEPS.DELIVERED_QTY: return 'Step 2: Enter Delivered Quantity';
      case STEPS.ALLOCATE: return 'Step 3: Allocate Remaining';
      case STEPS.REVIEW: return 'Step 4: Review Split';
      default: return 'Split Order';
    }
  };

  const renderContent = () => {
    switch (step) {
      case STEPS.LOADING:
        return <div className="loading-state"><LoadingSpinner /></div>;

      case STEPS.SUPPLIED_QTY:
        return renderSuppliedQtyStep();

      case STEPS.DELIVERED_QTY:
        return renderDeliveredQtyStep();

      case STEPS.ALLOCATE:
        return renderAllocateStep();

      case STEPS.REVIEW:
        return renderReviewStep();

      case STEPS.SPLITTING:
        return (
          <div className="loading-state">
            <span className="material-symbols-rounded spinning">sync</span>
            <span>Splitting order...</span>
          </div>
        );

      case STEPS.RESULT:
        return renderResultStep();

      default:
        return null;
    }
  };

  const renderSuppliedQtyStep = () => (
    <div className="split-step">
      {error && <div className="error-message">{error}</div>}
      <p className="step-description">
        Enter the quantity the supplier has confirmed they can supply for each product.
      </p>
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Product</th>
              <th className="text-right">Ordered Qty</th>
              <th className="text-right">Supplied Qty</th>
            </tr>
          </thead>
          <tbody>
            {order?.lines?.map(line => (
              <tr key={line.id}>
                <td>
                  {line.product_code && <span className="product-code">{line.product_code}</span>}
                  {line.product_name}
                </td>
                <td className="text-right">{formatQuantity(line.quantity)}</td>
                <td className="text-right">
                  <input
                    type="number"
                    className="qty-input"
                    value={suppliedQuantities[line.id] || ''}
                    onChange={(e) => setSuppliedQuantities(prev => ({ ...prev, [line.id]: e.target.value }))}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDeliveredQtyStep = () => (
    <div className="split-step">
      {error && <div className="error-message">{error}</div>}
      <p className="step-description">
        Enter the quantity delivered to <strong>{order?.customer_details?.company_name}</strong>.
        The remaining quantity will be allocated to other customers.
      </p>
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Product</th>
              <th className="text-right">Supplied</th>
              <th className="text-right">Delivered</th>
              <th className="text-right">Remaining</th>
            </tr>
          </thead>
          <tbody>
            {order?.lines?.map(line => {
              const supplied = parseFloat(suppliedQuantities[line.id] || line.supplied_quantity || 0);
              const delivered = parseFloat(deliveredQuantities[line.id] || 0);
              const remaining = supplied - delivered;
              return (
                <tr key={line.id}>
                  <td>
                    {line.product_code && <span className="product-code">{line.product_code}</span>}
                    {line.product_name}
                  </td>
                  <td className="text-right">{formatQuantity(supplied)}</td>
                  <td className="text-right">
                    <input
                      type="number"
                      className="qty-input"
                      value={deliveredQuantities[line.id] || ''}
                      onChange={(e) => setDeliveredQuantities(prev => ({ ...prev, [line.id]: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      max={supplied}
                    />
                  </td>
                  <td className={`text-right ${remaining > 0.0001 ? 'remaining-positive' : ''}`}>
                    {formatQuantity(remaining)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAllocateStep = () => (
    <div className="split-step">
      {error && <div className="error-message">{error}</div>}
      <p className="step-description">
        Allocate remaining quantities to other customers. Each allocation will create a new linked SO.
      </p>

      {splits.map((split, splitIndex) => (
        <div key={splitIndex} className="split-allocation">
          <div className="allocation-header">
            <h4>Customer {splitIndex + 1}</h4>
            {splits.length > 1 && (
              <button
                className="btn-icon btn-danger"
                onClick={() => handleRemoveSplit(splitIndex)}
                title="Remove"
              >
                <span className="material-symbols-rounded">close</span>
              </button>
            )}
          </div>

          <div className="customer-selector">
            <SearchableDropdown
              options={customers}
              value={split.accountId}
              onChange={(val) => handleSplitCustomerChange(splitIndex, val)}
              placeholder="Select customer..."
            />
          </div>

          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-right">Available</th>
                  <th className="text-right">Quantity</th>
                </tr>
              </thead>
              <tbody>
                {order?.lines?.filter(line => {
                  const supplied = parseFloat(suppliedQuantities[line.id] || line.supplied_quantity || 0);
                  const delivered = parseFloat(deliveredQuantities[line.id] || 0);
                  return supplied - delivered > 0.0001;
                }).map(line => {
                  const supplied = parseFloat(suppliedQuantities[line.id] || line.supplied_quantity || 0);
                  const delivered = parseFloat(deliveredQuantities[line.id] || 0);
                  const totalRemaining = supplied - delivered;
                  // Subtract quantities from other splits (not this one)
                  let otherAllocated = 0;
                  splits.forEach((s, i) => {
                    if (i !== splitIndex) {
                      otherAllocated += parseFloat(s.lines[line.id] || 0);
                    }
                  });
                  const available = totalRemaining - otherAllocated;

                  return (
                    <tr key={line.id}>
                      <td>
                        {line.product_code && <span className="product-code">{line.product_code}</span>}
                        {line.product_name}
                      </td>
                      <td className="text-right">{formatQuantity(available)}</td>
                      <td className="text-right">
                        <input
                          type="number"
                          className="qty-input"
                          value={split.lines[line.id] || ''}
                          onChange={(e) => handleSplitQtyChange(splitIndex, line.id, e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          max={available}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div style={{ alignSelf: 'end', textAlign: 'right' }}>
        <button className="btn-slideout" onClick={handleAddSplit}>
          <span className='icon-wrapper'>
            <span className="material-symbols-rounded">add</span>
          </span>
          <span className='btn-label'>Add Another Customer</span>
        </button>
      </div>

      {/* Show unallocated remainder */}
      {order?.lines?.some(line => {
        const remaining = getRemainingForLine(line.id);
        return remaining > 0.01;
      }) && (
        <div className="unallocated-warning">
          <span className="material-symbols-rounded">warning</span>
          <span>Some quantity is still unallocated. You can proceed, but unallocated product will not be assigned to any customer.</span>
        </div>
      )}
    </div>
  );

  const renderReviewStep = () => {
    const getCustomerName = (accountId) => {
      const customer = customers.find(c => c.value === accountId);
      return customer ? customer.label : 'Unknown';
    };

    return (
      <div className="split-step">
        {error && <div className="error-message">{error}</div>}
        <p className="step-description">Review the split before confirming.</p>

        <div className="review-section">
          <h4>
            <span className="material-symbols-rounded">person</span>
            {order?.customer_details?.company_name} (Original)
          </h4>
          <div className="table-wrapper">
            <table className="data-table compact">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-right">New Qty</th>
                  <th className="text-right">Value</th>
                </tr>
              </thead>
              <tbody>
                {order?.lines?.map(line => {
                  const qty = parseFloat(deliveredQuantities[line.id] || 0);
                  const value = qty * parseFloat(line.unit_price || 0);
                  return (
                    <tr key={line.id}>
                      <td>{line.product_name}</td>
                      <td className="text-right">{formatQuantity(qty)}</td>
                      <td className="text-right">{formatCurrency(value)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {splits.map((split, i) => (
          <div key={i} className="review-section split-review">
            <h4>
              <span className="material-symbols-rounded">person_add</span>
              {getCustomerName(split.accountId)} (New SO)
            </h4>
            <div className="table-wrapper">
              <table className="data-table compact">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="text-right">Qty</th>
                    <th className="text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(split.lines)
                    .filter(([_, qty]) => parseFloat(qty || 0) > 0)
                    .map(([lineId, qty]) => {
                      const line = order.lines.find(l => l.id === parseInt(lineId));
                      if (!line) return null;
                      const quantity = parseFloat(qty);
                      const value = quantity * parseFloat(line.unit_price || 0);
                      return (
                        <tr key={lineId}>
                          <td>{line.product_name}</td>
                          <td className="text-right">{formatQuantity(quantity)}</td>
                          <td className="text-right">{formatCurrency(value)}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <div className="split-notes-section">
          <label className="split-notes-label" htmlFor="split-notes-input">
            <span className="material-symbols-rounded">edit_note</span>
            Split notes <span className="optional-hint">(optional)</span>
          </label>
          <Textarea
            id="split-notes-input"
            value={splitNotes}
            onChange={(e) => setSplitNotes(e.target.value)}
            placeholder="Reason for splitting this order, e.g. &quot;Rep's customer ran out&quot;"
            rows={3}
            maxLength={1000}
          />
        </div>
      </div>
    );
  };

  const renderResultStep = () => {
    if (error && !result) {
      return (
        <div className="result-step">
          <div className="check-icon error">
            <span className="material-symbols-rounded">error</span>
          </div>
          <h3>Error</h3>
          <p>{error}</p>
          <div className="check-actions">
            <button data-read-safe className="btn-round btn-danger" onClick={handleClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Close</span>
            </button>
          </div>
        </div>
      );
    }

    if (result?.success) {
      return (
        <div className="result-step">
          <div className="check-icon success">
            <span className="material-symbols-rounded">check_circle</span>
          </div>
          <h3>Order Split Successfully</h3>
          <div className="result-details">
            <div className="result-item">
              <label>Original Order</label>
              <span>{result.originalOrder?.orderNumber} (quantities reduced)</span>
            </div>
            {result.splits?.map((split, i) => (
              <div key={i} className="result-item">
                <label>New SO #{i + 1}</label>
                <span className="split-result-number">
                  {split.orderNumber}
                  <span className="split-customer"> — {split.accountName}</span>
                </span>
              </div>
            ))}
          </div>
          <div className="check-actions">
            <button data-read-safe className="btn-slideout active" onClick={handleClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">done</span></div>
              <span className="btn-label">Done</span>
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderFooter = () => {
    switch (step) {
      case STEPS.SUPPLIED_QTY:
        return (
          <>
            <button data-read-safe className="btn-round btn-danger" onClick={handleClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
            <div className="footer-right">
              <button className="btn-slideout active" onClick={handleSaveSuppliedQty}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">check</span></div>
                <span className="btn-label">Continue</span>
              </button>
            </div>
          </>
        );

      case STEPS.DELIVERED_QTY:
        return (
          <>
            <button data-read-safe className="btn-round btn-danger" onClick={handleClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
            <div className="footer-right">
              <button className="btn-slideout active" onClick={handleDeliveredQtyNext}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">arrow_forward</span></div>
                <span className="btn-label">Continue</span>
              </button>
            </div>
          </>
        );

      case STEPS.ALLOCATE:
        return (
          <>
            <button data-read-safe className="btn-slideout" onClick={() => setStep(STEPS.DELIVERED_QTY)}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">arrow_back</span></div>
              <span className="btn-label">Back</span>
            </button>
            <div className="footer-right">
              <button
                className="btn-slideout active"
                onClick={() => { setError(null); setStep(STEPS.REVIEW); }}
                disabled={!canProceedToReview()}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">preview</span></div>
                <span className="btn-label">Review Split</span>
              </button>
            </div>
          </>
        );

      case STEPS.REVIEW:
        return (
          <>
            <button data-read-safe className="btn-slideout" onClick={() => setStep(STEPS.ALLOCATE)}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">arrow_back</span></div>
              <span className="btn-label">Back</span>
            </button>
            <div className="footer-right">
              <button className="btn-slideout active" onClick={handleConfirmSplit}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">call_split</span></div>
                <span className="btn-label">Confirm Split</span>
              </button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === STEPS.RESULT || step === STEPS.SPLITTING ? 'Split Order' : getStepTitle()}
      size="lg"
      footer={renderFooter()}
      className="split-order-modal"
    >
      {renderContent()}
    </Modal>
  );
}

export default SplitOrderModal;
